
<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import PreviewHeader from '@/components/PreviewHeader.vue';
import InlineSpinner from '@/components/InlineSpinner.vue';
import LoadingOverlay from '@/components/LoadingOverlay.vue';
import { useUserStore } from '@/stores/user';
import { useBusinessPartnersListsStore } from '@/stores/businessPartnersLists';
import { useBusinessPartnersStore } from '@/stores/businessPartners';
import { useBusinessPartnersCountStore } from '@/stores/businessPartnersCount';
import wholeWordIcon from '@/assets/imgs/icons/whole-word.svg';

import LeadListDropdown from '@/components/LeadListDropdown.vue';
import FilterSummaryChips from '@/components/FilterSummaryChips.vue';



const router = useRouter();
const userStore = useUserStore();
const businessPartnersListsStore = useBusinessPartnersListsStore();
const businessPartnersStore = useBusinessPartnersStore();
const filteredListSize = computed(() => businessPartnersStore.businessPartnerList.size);
const businessPartnersCountStore = useBusinessPartnersCountStore();

// Speichert die Signatur des letzten echten Nutzer-Filters (nur wenn vom User gesetzt)
const lastUserFilterSignature = ref(localStorage.getItem('lastUserFilterSignature') || null);

// Beobachte Änderungen am Filter und aktualisiere lastUserFilterSignature nur bei echter Filteranwendung
watch(
        () => userStore.currentFilter.filter.signature,
        (newVal, oldVal) => {
            if (!newVal || newVal === oldVal) return;
            // Wenn die neue Signatur mit dem generierten Initialfilter übereinstimmt,
            // behandeln wir sie als System-Filter und speichern sie NICHT als Nutzer-Filter.
            if (initialFilterSignature.value && newVal === initialFilterSignature.value) {
                lastUserFilterSignature.value = null;
                localStorage.removeItem('lastUserFilterSignature');
                return;
            }
            // Fallback: ältere Heuristik, falls initialFilterSignature nicht verfügbar
            if (isInitialFilterSignature(newVal)) {
                lastUserFilterSignature.value = null;
                localStorage.removeItem('lastUserFilterSignature');
            } else {
                // Nur echte Nutzerfilter speichern
                lastUserFilterSignature.value = newVal;
                localStorage.setItem('lastUserFilterSignature', newVal);
            }
        }
);

// Liefert das Route-Objekt für die initiale Filter-Navigation
const initialFilterRoute = computed(() => {
    // IDs müssen immer gesetzt sein, sonst kein Routing
    const salesAgentId = userStore.currentSelection.salesAgent?.id;
    const salesTeamId = userStore.currentSelection.salesTeam?.id;
    const campaignId = userStore.currentSelection.campaign?.id;
    if (
        typeof salesAgentId !== 'number' ||
        typeof salesTeamId !== 'number' ||
        typeof campaignId !== 'number' ||
        isNaN(salesAgentId) ||
        isNaN(salesTeamId) ||
        isNaN(campaignId)
    ) {
        // Fallback: leeres Objekt, damit :to niemals undefined ist
        return { name: '', query: {} };
    }
    const filterSignature = [
        `idBusinessPartnerList:-1`,
        `idBusinessPartnerStatus:null`,
        `idBusinessPartnerPotential:-1`,
        `idBusinessPartnerProduct:[]`,
        `idBusinessPartnerProductSegment:[]`,
        `idBusinessPartnerCityZip:-1`,
        `nameBusinessPartnerSector:`,
        `idBusinessPartnerOrder:-1`,
        `${salesAgentId}:${salesTeamId}:${campaignId}`
    ].join('|');
    return {
        name: 'BusinessPartnerList',
        query: {
            source: 'filter',
            hideFilter: 'true',
            filter: filterSignature
        }
    };
});

// Initialfilter-Signatur als klaren Vergleichswert (falls vorhanden)
const initialFilterSignature = computed(() => {
    try {
        return initialFilterRoute.value.query.filter || null;
    } catch (e) {
        return null;
    }
});

function isInitialFilterSignature(signature) {
    // Prüfe auf alle bekannten Initialfilter-Felder (auch optionale Felder, die im Systemfilter vorkommen)
    if (!signature) return false;
    const mustHaveParts = [
        'idBusinessPartnerStatus:null',
        'idBusinessPartnerList:-1',
        'idBusinessPartnerProduct:[]',
        'idBusinessPartnerProductSegment:[]',
        'idBusinessPartnerPotential:-1',
        'idBusinessPartnerOrder:-1'
    ];
    // Optionale Felder, die im Initialfilter oft vorkommen
    const optionalParts = [
        'idBusinessPartnerAboStatus:-1',
        'idBusinessPartnerCityZip:-1',
        'nameBusinessPartnerSector:',
        'businessPartnerSalesPitchStatus:{"id":-1',
        'invoice:{"dateStart":null',
        'productGroupStates:',
        'revenueMin:null',
        'revenueMax:null',
        'search:{"mode":"name","string":"","wholeWord":0}',
        'salesValues:{"total":null',
    ];
    // IDs am Ende prüfen (Format: |<agent>:<team>:<campaign>)
    const idPattern = /\|\d+:\d+:\d+$/;
    // Muss alle Pflichtteile enthalten und IDs am Ende
    if (!mustHaveParts.every(part => signature.includes(part)) || !idPattern.test(signature)) return false;
    // Wenn KEINER der optionalParts fehlt, ist es ein Initialfilter
    const missingOptionals = optionalParts.filter(part => !signature.includes(part));
    // Wenn mindestens 5 optionale Teile enthalten sind, ist es sehr sicher ein Initialfilter
    if (optionalParts.length - missingOptionals.length >= 5) return true;
    // Fallback: Wenn alle Pflichtteile + IDs stimmen und KEIN user-spezifisches Feld (z.B. search mit Wert, andere Felder gesetzt)
    return false;
}

// State
const businessPartnerSearch = ref('');
const searchLoading = ref(false);
let searchSafetyTimer = null;
let pendingRoute = null;
let pendingRouteTimer = null;
const searchError = ref(false);
// Zustände für leeren/fehlerhaften Suchzustand
const hasContent = ref(false);
const hasNoResults = computed(() => {
    return !!(userStore.currentSelection.businessPartnerList.search?.error) && hasContent.value;
});
const lastQuery = ref('');
let searchTimer = null; // reserved for future debounce
const leadGenerationQuery = ref('');
let timerCounts = null;
// Lokales Loading nur für das "Unb. Sugar Aktionen"-Dropdown
const isLeadListsLoading = ref(true);

// Loading-Status für businessPartnerListCount aus dem Store holen
const bpListCountSignature = computed(() => `${userStore.currentSelection.salesAgent.id}:${userStore.currentSelection.campaign.id}#businessPartnerListCount`);
const isBpListCountLoading = computed(() => businessPartnersCountStore.isLoadingBySignature(bpListCountSignature.value));

// Watcher für SalesAgent und SalesTeam:
// Nur den spezifischen Potential-&-BK-Count nachladen, die allgemeinen Today/ToDo-Counts
// werden zentral über userStore/businessPartnersCountStore.refresh() aktualisiert.
watch(
    () => [userStore.currentSelection.salesAgent.id, userStore.currentSelection.salesTeam?.id],
    async ([newAgentId, newTeamId], [oldAgentId, oldTeamId]) => {
        // Bei Wechsel der Zuordnung nur den businessPartnerListCount für Potential & BK aktualisieren
        await businessPartnersCountStore.getByFilter(businessPartnersCountStore.namedFilter('businessPartnerListCount'));
    },
    { immediate: false }
);

onMounted(async() => {
    console.log('[WidgetNav onMounted] currentFilter:', userStore.currentFilter);
    console.log('[WidgetNav onMounted] lastUserFilterSignature:', lastUserFilterSignature.value);
    console.log('[WidgetNav onMounted] businessPartnerLists:', businessPartnersStore.businessPartnerLists);
    console.log('[WidgetNav onMounted] isFilterButtonActive:', isFilterButtonActive.value);
    if (userStore.currentSelection.campaign.id===null) await userStore.selectCampaign(userStore.idCampaignLogin);
    // Stelle sicher, dass Filteroptionen geladen sind, bevor Lead-Listen geladen werden,
    // aber ohne dabei den bestehenden BusinessPartner-Filter zurückzusetzen.
    if (!userStore.filterOption || Object.keys(userStore.filterOption).length === 0) {
        if (typeof userStore.loadFilterOptions === 'function') {
            await userStore.loadFilterOptions();
        } else if (typeof userStore.refreshFilterOptions === 'function') {
            await userStore.refreshFilterOptions({ resetFilters: false });
        }
    }
    await businessPartnersCountStore.refresh();
    // Ensure lead lists are loaded for dropdown – aber nur,
    // wenn für die aktuelle Kampagne/Agentur noch keine Daten im Store liegen.
    const existingLeadLists = businessPartnersListsStore.businessPartnersLists?.data || [];
    if (!Array.isArray(existingLeadLists) || existingLeadLists.length === 0) {
        try {
            await businessPartnersListsStore.refresh();
        } catch(e) {
            // ignore, Anzeige übernimmt später die leere-States
        } finally {
            isLeadListsLoading.value = false;
        }
    } else {
        // Es liegen bereits Lead-Listen im Store vor (z.B. nach vorherigem Dashboard-Besuch),
        // daher kein erneutes Laden und Loader direkt ausblenden.
        isLeadListsLoading.value = false;
    }
    // Hole Gesamtzahl aller BusinessPartner-Listen nur, wenn noch kein Count für diese Signatur existiert
    const existingBpListCount = businessPartnersCountStore.businessPartnerLists
        ? businessPartnersCountStore.businessPartnerLists.find((entry) => entry.signature === bpListCountSignature.value)
        : null;
    if (!existingBpListCount) {
        await businessPartnersCountStore.getByFilter(businessPartnersCountStore.namedFilter('businessPartnerListCount'));
    }
    timerCounts = setInterval(async () => { await businessPartnersCountStore.refresh(); }, 1000 * 60 * 5); // alle 5 Minuten
    // Wenn im localStorage die System-Initialfilter-Signatur steckt, entferne sie
    try {
        if (lastUserFilterSignature.value && initialFilterSignature.value && lastUserFilterSignature.value === initialFilterSignature.value) {
            lastUserFilterSignature.value = null;
            localStorage.removeItem('lastUserFilterSignature');
            console.debug('[PreviewBP] Removed system signature from lastUserFilterSignature on mount');
        }
    } catch (e) {}
});

onUnmounted (() => { if (timerCounts) clearInterval(timerCounts); })


    // Neuer Suchmodus (GPNo, name, tel) mit lowercase Werten (außer GPNo)
    const businessPartnerSearchMode = ref('name');
    const searchModeOptions = [
        { label: 'GPNo', value: 'GPNo' },
        { label: 'Name', value: 'name' },
        { label: 'Tel', value: 'tel' }
    ];
    const wholeWord = ref(false); // Toggle für "Ganzes Wort" Suche
    // MatchCase entfernt (Backend-Anforderung: Flag entfällt)
    const searchPlaceholder = computed(() => {
        switch (businessPartnerSearchMode.value) {
            case 'GPNo': return 'GP Nummer …';
            case 'tel': return 'Telefon …';
            case 'name':
            default: return 'Name / Firma …';
        }
    });
    // Alias to match filter component naming
    const searchPlaceholderText = computed(() => searchPlaceholder.value);
    // Input Sanitizing: Nur Zahlen für GPNo & Tel
    const sanitizedInput = computed(() => {
        if (['tel'].includes(businessPartnerSearchMode.value)) {
            return businessPartnerSearch.value.replace(/[^0-9+\s\-\/]/g,'');
        }
        return businessPartnerSearch.value;
    });
    watch(sanitizedInput, (val) => {
        if (val !== businessPartnerSearch.value) businessPartnerSearch.value = val;
    });
    watch(businessPartnerSearch, (val) => {
        if (/^[+0][0-9\s\-\/]*$/.test(val)) {
            // Auto-Switch auf tel bei 0 oder '+'
            businessPartnerSearchMode.value = 'tel';
        } else if (/^([1-9]+|[a-zA-Z]{3}-?)[0-9]*$/.test(val)) {
            // Auto-Switch auf GPNo bei Input der mit 3 Buchstaben + Bindestrich oder zifferen von 0-9 beginnt und sonst nur aus Ziffern besteht
            businessPartnerSearchMode.value = 'GPNo';
        } else {
            businessPartnerSearchMode.value = 'name';
        }
    });
    // Validierung
    const isValidSearch = computed(() => {
        const term = sanitizedInput.value.trim();
        if (businessPartnerSearchMode.value === 'GPNo') return term.length >= 3;
        if (businessPartnerSearchMode.value === 'tel') return term.length >= 6;
        return term.length >= 2; // name
    });
    const isSearching = ref(false);
    // Entfernt: Recent Searches Persistence & Chips
    function clearSearch(){
        businessPartnerSearch.value='';
        userStore.currentSelection.businessPartnerList.search.error=false;
    }

    // Trackt, ob überhaupt Inhalt eingegeben wurde, um "keine Ergebnisse" visuell kennzeichnen zu können
    watch(businessPartnerSearch, (newValue) => {
        if ((newValue || '').trim() === '') {
            hasContent.value = false;
            searchError.value = false;
            try {
                if (userStore.currentSelection?.businessPartnerList?.search) {
                    userStore.currentSelection.businessPartnerList.search.error = false;
                }
            } catch(e) { /* noop */ }
        } else {
            hasContent.value = true;
            searchError.value = false;
        }
    });

    // Recent searches state
    const recentSearches = ref([]);
    const BP_MAX_RECENT = 10;

    // Bedingungen aus PreviewBusinessPartnerList_new übernommen
    const quickActions = computed(()=> {
        const signatureBase = `${userStore.currentSelection.salesAgent.id}:${userStore.currentSelection.campaign.id}`;
        const campaignEndRaw = userStore.currentSelection.campaign.dateEnd;
        const campaignEnd = campaignEndRaw ? new Date(campaignEndRaw) : null;
        const now = new Date();
        const beforeCampaignEnd = campaignEnd ? (now <= campaignEnd) : false;
        if (campaignEndRaw) {
            // Debug-Ausgabe für Datumsvergleich
            // eslint-disable-next-line no-console
            console.debug('[PreviewBP] now:', now.toISOString(), 'campaignEnd:', campaignEnd.toISOString(), 'beforeCampaignEnd:', beforeCampaignEnd);
        }
        const count = (suffix) => businessPartnersCountStore.businessPartnersListsCountBySignature(`${signatureBase}${suffix}`) || 0;
        const todayFollowUps = count(`#Today:WiedervorlagenCount`);
        const todayAppointments = count(`#Today:VorOrtTermineCount`);
        const toDoFollowUps = count(`#ToDo:WiedervorlagenCount`);
        const toDoAppointments = count(`#ToDo:VorOrtTermineCount`);
        return [
            {
                key:'todayFollowUps',
                label:'WVL Heute',
                count: todayFollowUps,
                to: { path:'/businessPartnerList', query:{ source:'filter', namedFilter:'Today:Wiedervorlagen', hideFilter:'true'}},
                loading: isLoadingTodayFollowUps.value,
                disabled: isLoadingTodayFollowUps.value || todayFollowUps===0,
                negative: false,
                max: null
            },
            {
                key:'todayAppointments',
                label:'Termine Heute',
                count: todayAppointments,
                to: { path:'/businessPartnerList', query:{ source:'filter', namedFilter:'Today:VorOrtTermine', hideFilter:'true'}},
                loading: isLoadingTodayAppointments.value,
                disabled: isLoadingTodayAppointments.value || todayAppointments===0,
                negative: false,
                max: null
            },
            {
                key:'followUpTodo',
                label: beforeCampaignEnd ? 'WVL To-Do' : 'WVL Überfällig',
                count: toDoFollowUps,
                to: beforeCampaignEnd
                    ? { path:'/salesPitchFollowUp' }
                    : { path:'/businessPartnerList', query:{ source:'filter', namedFilter:'ToDo:Wiedervorlagen', hideFilter:'true'}},
                loading: isLoadingToDoFollowUps.value,
                disabled: isLoadingToDoFollowUps.value || toDoFollowUps===0,
                negative: (!userStore.isSalesTeamManager && !userStore.isManagement && beforeCampaignEnd) ? (toDoFollowUps > userStore.maxToDos.followUp) : false,
                max: (!userStore.isSalesTeamManager && !userStore.isManagement && beforeCampaignEnd) ? userStore.maxToDos.followUp : null
            },
            {
                key:'terminFeedback',
                label:'Termin-Feedback',
                count: toDoAppointments,
                to: { path:'/salesPitchFeedback' },
                loading: isLoadingToDoAppointments.value,
                disabled: isLoadingToDoAppointments.value || toDoAppointments===0,
                negative: (!userStore.isSalesTeamManager && !userStore.isManagement && beforeCampaignEnd) ? (toDoAppointments > userStore.maxToDos.feedback) : false,
                max: beforeCampaignEnd && (!userStore.isSalesTeamManager && !userStore.isManagement) ? userStore.maxToDos.feedback : 0
            }
        ];
    });

    const exceededToDos = computed(() => {
        if (userStore.isManagement || userStore.isSalesTeamManager || userStore.isWinback) return {
            both: false,
            followUps: false,
            feedbacks: false
        };
        const qa = quickActions.value;
        const followUp = qa.find(q => q.key === 'followUpTodo');
        const feedback = qa.find(q => q.key === 'terminFeedback');
        if (!followUp || !feedback) return { both: false, followUps: false, feedbacks: false };
        if (followUp.max !== null && feedback.max !== null) {
            return {
                both: (followUp.count > followUp.max) && (feedback.count > feedback.max),
                followUps: followUp.count > followUp.max,
                feedbacks: feedback.count > feedback.max,
            };
        } else if (feedback.max !== null) {
            // After campaign end, nur Feedback relevant
            return {
                both: false,
                followUps: false,
                feedbacks: feedback.count > feedback.max,
            };
        } else {
            return { both: false, followUps: false, feedbacks: false };
        }
    });

    const isLoadingList = computed(() => {
        return (userStore.currentSelection.businessPartnerList.isLoading || userStore.currentSelection.businessPartner.isLoading);
    });

    const isLoadingCount = computed(() => {
        return userStore.currentSelection.businessPartnerCount.isLoading === true;
    });

    const isLoadingTodayFollowUps = computed(() => {
        const signature = `${userStore.currentSelection.salesAgent.id}:${userStore.currentSelection.campaign.id}#Today:WiedervorlagenCount`;
        const hasEntry = Array.isArray(businessPartnersCountStore.businessPartnerLists)
            && businessPartnersCountStore.businessPartnerLists.some(entry => entry.signature === signature);
        // Initial: noch kein Eintrag => als "lädt" anzeigen, bis der erste Count da ist
        return !hasEntry || businessPartnersCountStore.isLoadingBySignature(signature);
    });

    const isLoadingToDoFollowUps = computed(() => {
        const signature = `${userStore.currentSelection.salesAgent.id}:${userStore.currentSelection.campaign.id}#ToDo:WiedervorlagenCount`;
        const hasEntry = Array.isArray(businessPartnersCountStore.businessPartnerLists)
            && businessPartnersCountStore.businessPartnerLists.some(entry => entry.signature === signature);
        return !hasEntry || businessPartnersCountStore.isLoadingBySignature(signature);
    });

    const isLoadingTodayAppointments = computed(() => {
        const signature = `${userStore.currentSelection.salesAgent.id}:${userStore.currentSelection.campaign.id}#Today:VorOrtTermineCount`;
        const hasEntry = Array.isArray(businessPartnersCountStore.businessPartnerLists)
            && businessPartnersCountStore.businessPartnerLists.some(entry => entry.signature === signature);
        return !hasEntry || businessPartnersCountStore.isLoadingBySignature(signature);
    });

    const isLoadingToDoAppointments = computed(() => {
        const signature = `${userStore.currentSelection.salesAgent.id}:${userStore.currentSelection.campaign.id}#ToDo:VorOrtTermineCount`;
        const hasEntry = Array.isArray(businessPartnersCountStore.businessPartnerLists)
            && businessPartnersCountStore.businessPartnerLists.some(entry => entry.signature === signature);
        return !hasEntry || businessPartnersCountStore.isLoadingBySignature(signature);
    });

    // Handle the form submissions of the text search
    const handleBusinessPartnerSearch = async () => {
        const term = (sanitizedInput.value || '').trim();
        if (!term || !isValidSearch.value || isSearching.value) return;
        const mode = businessPartnerSearchMode.value; // 'GPNo' | 'name' | 'tel'
        const wholeWordFlag = wholeWord.value ? 1 : 0;
        const searchString = term; // unverändert übergeben
        isSearching.value = true;
        try {
            // Diese Suche soll immer auf dem initialen Standardfilter laufen
            // und nicht auf einem zuvor gespeicherten/angepassten Filter.
            if (typeof userStore.resetBusinessPartnerListFilter === 'function') {
                userStore.resetBusinessPartnerListFilter(false);
            }
            // Integrierte Suche via getListByFilter: Filter.search setzen und normalen Filter-Submit triggern
            userStore.currentSelection.businessPartnerList.filter.search = {
                mode,
                string: searchString,
                wholeWord: wholeWordFlag
            };
            await router.push({ name: 'BusinessPartnerList', query: { source: 'filter', filter: lastUserFilterSignature.value || userStore.currentFilter.filter.signature } });
            // Recent searches aktualisieren
            lastQuery.value = searchString;
            const existsIdx = recentSearches.value.findIndex(q => q.toLowerCase() === searchString.toLowerCase());
            if (existsIdx !== -1) {
                recentSearches.value.splice(existsIdx, 1);
            }
            recentSearches.value.unshift(searchString);
            if (recentSearches.value.length > BP_MAX_RECENT) recentSearches.value.pop();
            persistRecent();
        } finally {
            isSearching.value = false;
        }
    };
    // Define a function to handle form submissions
    const handleLeadGeneration = () => {
        // console.log('searching for:', leadGenerationQuery.value, userStore.currentSelection.leadGenerationQuery.isLoading);
        router.push({ name: 'LeadGeneration', query: { source: 'search', searchMode: 'all', search: leadGenerationQuery.value}});
    };
    const handleSelectList = (idList) => {
        toggleDropdown();
        // console.log('routing to list:', idList);
        // Für Lead-Listen immer vom Standard-Filter aus starten, damit
        // keine zuvor gesetzten Kriterien (z.B. Umsatz, Potential) mitwirken.
        if (typeof userStore.resetBusinessPartnerListFilter === 'function') {
            userStore.resetBusinessPartnerListFilter(false);
        }
        // set the page to 1 to avoid pagination issues
        userStore.currentSelection.businessPartnerList.page = 1;
        userStore.currentSelection.businessPartnerList.pageSize = 24;
        userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerList = idList;
        // Bei Lead-Aktionen darf keine freie Textsuche aktiv sein
        if (userStore.currentSelection.businessPartnerList.filter.search) {
            delete userStore.currentSelection.businessPartnerList.filter.search;
        }
        router.push({ name: 'BusinessPartnerList', query: { source: 'filter', filter: `idBusinessPartnerStatus:1|idBusinessPartnerAboStatus:-1|idBusinessPartnerList:${idList}|idBusinessPartnerProduct:-1|idBusinessPartnerPotential:-1|idBusinessPartnerCityZip:-1|nameBusinessPartnerSector:|idBusinessPartnerOrder:-1` }});
    };

    const dropdownVisible = ref(false);
    const hasLeadLists = computed(()=> (Array.isArray(leadListOptions.value) ? leadListOptions.value.length : 0) > 0);
    const selectedLeadList = ref(null);
    const fallbackLeadLists = computed(() => {
        const raw = userStore.filterOption?.businessPartnerLists || [];
        if (!Array.isArray(raw)) return [];
        return raw.map(l => ({
            id: l.id,
            name: l.name,
            businessPartnerCount: l.countBusinessPartner || l.businessPartnerCount || 0
        })).filter(l => l.id !== undefined && l.name && (l.businessPartnerCount > 0));
    });
    const leadListOptions = computed(() => {
        const loadedRaw = businessPartnersListsStore.businessPartnersLists?.data || [];
        const loaded = Array.isArray(loadedRaw)
            ? loadedRaw.map(l => ({
                id: l.id ?? l.idBusinessPartnerList ?? l.listId,
                name: l.name ?? l.nameBusinessPartnerList ?? l.listName,
                businessPartnerCount: l.businessPartnerCount ?? l.countBusinessPartner ?? l.totalCount ?? 0
            })).filter(l => l.id !== undefined && l.name && (l.businessPartnerCount > 0))
            : [];

        const signature = `${userStore.currentSelection.salesAgent.id}:${userStore.currentSelection.campaign.id}#LeadList`;
        const signatureListRaw = businessPartnersStore.businessPartnerListBySignature(signature)?.data || [];
        const signatureList = Array.isArray(signatureListRaw)
            ? signatureListRaw.map(l => ({
                id: l.id ?? l.idBusinessPartnerList ?? l.listId,
                name: l.name ?? l.nameBusinessPartnerList ?? l.listName,
                businessPartnerCount: l.businessPartnerCount ?? l.countBusinessPartner ?? l.totalCount ?? 0
            })).filter(l => l.id !== undefined && l.name && (l.businessPartnerCount > 0))
            : [];

        const fallback = fallbackLeadLists.value || [];
        try {
            console.debug('[PreviewBP] lead list sources', {
                loadedLen: Array.isArray(loadedRaw) ? loadedRaw.length : -1,
                signatureLen: Array.isArray(signatureListRaw) ? signatureListRaw.length : -1,
                fallbackLen: Array.isArray(fallback) ? fallback.length : -1,
                signature
            });
        } catch(e) {}
        if (Array.isArray(loaded) && loaded.length > 0) return loaded;
        if (Array.isArray(signatureList) && signatureList.length > 0) return signatureList;
        return fallback;
    });
    watch(leadListOptions, (val) => {
        if (!selectedLeadList.value && Array.isArray(val) && val.length === 1) selectedLeadList.value = val[0];
    }, { immediate: true });
    const toggleDropdown = () => { dropdownVisible.value = !dropdownVisible.value };
    function selectLeadList(list){
        if(!list) return;
        selectedLeadList.value = list;
        handleSelectList(`list:${list.id}`);
    }
    // QuickActions (WVL Heute, Termine Heute, ToDos) sollen ebenfalls immer
    // auf einem frischen Standard-Filter laufen und nicht mit zuvor
    // gesetzten Kriterien kombiniert werden.
    async function handleQuickAction(qa) {
        window.handleQuickActionCalled = true;
        console.log('[Post] handleQuickAction: Funktion wurde aufgerufen', qa);
        if (!qa || qa.disabled || qa.loading) return;
        if (typeof userStore.resetBusinessPartnerListFilter === 'function') {
            userStore.resetBusinessPartnerListFilter(false);
        }
        // Spezialfall: Today-Buttons (Wiedervorlagen/Termine) – Filter mit Datum im businessPartnerSalesPitchStatus
        if (qa.to && qa.to.query && (qa.to.query.namedFilter === 'Today:VorOrtTermine' || qa.to.query.namedFilter === 'Today:Wiedervorlagen')) {
            const today = new Date().toISOString().split('T')[0];
            // Hole das aktuelle Filterobjekt
            const filter = { ...userStore.currentSelection.businessPartnerList.filter };
            // Setze NUR im businessPartnerSalesPitchStatus das Datum
            filter.businessPartnerSalesPitchStatus = {
                ...(filter.businessPartnerSalesPitchStatus || {}),
                id: qa.to.query.namedFilter === 'Today:VorOrtTermine' ? 3 : 2,
                dateStart: today,
                dateEnd: today
            };
            // Setze das Filterobjekt explizit im Store
            userStore.currentSelection.businessPartnerList.filter = filter;
            console.log('[Post] handleQuickAction: Filter für Today-Button gebaut:', filter);
            const navTarget = {
                name: 'BusinessPartnerList',
                query: {
                    source: 'filter',
                    hideFilter: 'true',
                    filter: JSON.stringify(filter)
                }
            };
            console.log('[Post] handleQuickAction: Router-Navigation ausgeführt mit Query:', navTarget);
            await router.push(navTarget);
            return;
        }
        console.log('[Post] handleQuickAction: Standard-Routing für QA', qa);
        await router.push(qa.to);
    }

    // Explizit exportieren, damit Vue sie im Template korrekt findet
    // (bei <script setup> ist das normalerweise nicht nötig, aber für Debugging sicherstellen)
    // window.handleQuickAction = handleQuickAction;
// Restore recent searches persistence
try {
    const raw = localStorage.getItem('bp_recentSearches');
    if(raw){ const arr = JSON.parse(raw); if(Array.isArray(arr)) recentSearches.value = arr.slice(0, BP_MAX_RECENT); }
} catch(e){ console.warn('Failed to parse bp_recentSearches', e); }
function persistRecent(){ try{ localStorage.setItem('bp_recentSearches', JSON.stringify(recentSearches.value)); }catch(e){} }
// Recent search helpers
function handleRecentClick(q){
    if (!q || isSearching.value) return;
    const term = String(q || '');
    businessPartnerSearch.value = term;
    // Modus explizit anhand des gespeicherten Suchbegriffs bestimmen,
    // statt auf den asynchronen Watcher zu warten.
    if (/^[+0][0-9\s\-\/]*$/.test(term)) {
        // Tel: beginnt mit 0 oder + und nur Ziffern/Leerzeichen/Trennzeichen
        businessPartnerSearchMode.value = 'tel';
    } else if (/^([1-9]+|[a-zA-Z]{3}-?)[0-9]*$/.test(term)) {
        // GPNo: nur Ziffern oder 3 Buchstaben (+ optional '-') gefolgt von Ziffern
        businessPartnerSearchMode.value = 'GPNo';
    } else {
        businessPartnerSearchMode.value = 'name';
    }
    lastQuery.value = term;
    handleBusinessPartnerSearch();
}
function removeRecentSearch(q){
    const idx = recentSearches.value.findIndex(item => item.toLowerCase() === String(q).toLowerCase());
    if (idx !== -1){ recentSearches.value.splice(idx,1); persistRecent(); }
}
function clearRecentSearches(){ recentSearches.value = []; persistRecent(); }

function formatCountWithThousands(count) {
    if (typeof count === 'number') {
        return count.toLocaleString('de-DE');
    }
    const num = Number(count);
    if (!isNaN(num)) {
        return num.toLocaleString('de-DE');
    }
    return count;
}

// Button-Disable-Logik und Grundtext
    const isPotentialBKDisabled = computed(() => {
        if (exceededToDos.value && (exceededToDos.value.feedbacks || exceededToDos.value.followUps)) return true;
        if (isLoadingList.value || isBpListCountLoading.value) return true;
        const count = businessPartnersCountStore.businessPartnersListsCountBySignature(bpListCountSignature.value);
        if (typeof count === 'undefined' || count === 0) return true;
        return false;
    });

const potentialBKDisabledReason = computed(() => {
    if (exceededToDos.value && (exceededToDos.value.feedbacks || exceededToDos.value.followUps)) {
        if (exceededToDos.value.both) {
            return `Maximale Anzahl unbearbeiteter Termine (${quickActions.value.find(q => q.key === 'terminFeedback')?.max}) und Wiedervorlagen (${quickActions.value.find(q => q.key === 'followUpTodo')?.max}) erreicht.`;
        } else if (exceededToDos.value.followUps) {
            return `Maximale Anzahl unbearbeiteter Wiedervorlagen (${quickActions.value.find(q => q.key === 'followUpTodo')?.max}) erreicht.`;
        } else {
            return `Maximale Anzahl unbearbeiteter Termine (${quickActions.value.find(q => q.key === 'terminFeedback')?.max}) erreicht.`;
        }
    }
    if (isLoadingList.value) return 'Daten werden geladen…';
    if (businessPartnersCountStore.businessPartnersListsCountBySignature(`${userStore.currentSelection.salesAgent.id}:${userStore.currentSelection.campaign.id}#businessPartnerListCount`) === 0) return 'Keine BusinessPartner-Listen vorhanden.';
    return '';
});


// Liefert die letzte echte User-Filter-Liste (summaryObj für Chips, summaryObj[0].value für Treffer)

// Chips und Trefferzahl immer aus dem aktuell angezeigten Filter (businessPartnerList)
const currentBusinessPartnerList = computed(() => businessPartnersStore.businessPartnerList);
const currentBusinessPartnerListCount = computed(() => {
    const entry = currentBusinessPartnerList.value;
    if (entry && Array.isArray(entry.summaryObj) && entry.summaryObj.length > 0) {
        return entry.summaryObj[0].value || 0;
    }
    return 0;
});
const currentBusinessPartnerListChips = computed(() => {
    const entry = currentBusinessPartnerList.value;
    return (entry && Array.isArray(entry.summaryObj)) ? entry.summaryObj : [];
});

// Toggle für die Chips in der Card
import { ref as vueRef } from 'vue';
const showChips = vueRef(false);

// Bestimme die letzte vom USER gesetzte gefilterte Liste (aus dem Store)
// Suche zuerst anhand der gespeicherten Signatur, dann nach isUserFilter === true als Fallback
const lastUserFilterEntry = computed(() => {
    const lists = businessPartnersStore.businessPartnerLists;
    if (!Array.isArray(lists) || lists.length === 0) return null;
    // Nimm immer den zuletzt aktualisierten Eintrag mit isUserFilter === true
    // (nicht den mit passender Signatur aus localStorage – der könnte veraltet sein)
    let bestEntry = null;
    let bestUpdate = -1;
    for (const entry of lists) {
        if (entry && entry.isUserFilter === true) {
            const upd = typeof entry.update === 'number' ? entry.update : 0;
            if (upd > bestUpdate) { bestEntry = entry; bestUpdate = upd; }
        }
    }
    if (bestEntry) return bestEntry;
    // Fallback nach Page-Reload (Store leer): aus localStorage
    const sig = lastUserFilterSignature.value;
    if (sig) return lists.find(e => e && e.signature === sig) || null;
    return null;
});

// Zahl aus summaryObj ("Treffer"-Objekt) extrahieren – KEIN businessPartnersCountStore
const lastBusinessPartnerListCount = computed(() => {
    const entry = lastUserFilterEntry.value;
    if (!entry) return 0;
    if (Array.isArray(entry.summaryObj)) {
        const trefferObj = entry.summaryObj.find(o => o.name === 'Treffer');
        if (trefferObj && trefferObj.value !== undefined) {
            // Handle German locale format: "1.800" → 1800
            const cleaned = String(trefferObj.value).replace(/\./g, '').replace(/,/g, '.');
            const val = parseInt(cleaned, 10);
            if (!isNaN(val)) return val;
        }
    }
    // Fallback: size-Feld aus dem Eintrag
    return (typeof entry.size === 'number' ? entry.size : 0);
});

// Chips (summaryObj) from the last USER filter entry
const lastUserFilterChips = computed(() => {
    const entry = lastUserFilterEntry.value;
    return (entry && Array.isArray(entry.summaryObj)) ? entry.summaryObj : [];
});

// Fullscreen Loading Overlay für Navigation
const showFullscreenLoading = ref(false);


// Navigiert zur Liste mit initialem Filter (ohne den Store zu verändern)
async function handleWidgetNavigationInitialFilter() {
    // User-Filter sofort löschen, damit die Karte "Noch keinen Filter verwendet" zeigt
    lastUserFilterSignature.value = null;
    localStorage.removeItem('lastUserFilterSignature');
    showFullscreenLoading.value = true;
    try {
        // Hole explizit den Initialfilter aus dem Store
        const initialFilter = businessPartnersStore.namedFilter('initialFilter');
        // Lade die Daten mit dem Initialfilter
        await businessPartnersStore.getByFilter(initialFilter, [], false, { userApplied: false });
        // Setze den Filter im userStore
        userStore.currentSelection.businessPartnerList.filter = { ...initialFilter };
        await router.push({
            name: 'BusinessPartnerList',
            query: {
                source: 'filter',
                hideFilter: 'true',
                filter: initialFilter.signature
            }
        });
        // Nach Navigation: User-Filter-Referenz zurücksetzen, damit Chips und Treffer synchron sind
        lastUserFilterSignature.value = null;
        localStorage.removeItem('lastUserFilterSignature');
    } finally {
        showFullscreenLoading.value = false;
    }
}

// Navigiert zur gespeicherten Filter-Liste
function goToSavedFilterList() {
    if (isBpListCountLoading.value || isPotentialBKDisabled.value) return;
    router.push({
        name: 'BusinessPartnerList',
        query: {
            source: 'filter',
            hideFilter: 'true',
            filter: userStore.currentFilter.filter.signature
        }
    });
}

// Navigation für die Card 'Zur gefilterten Liste'
async function handleWidgetNavigation(to) {
        // Spezialfall: Today-Buttons (Wiedervorlagen/Termine) – IMMER den Systemfilter aus dem Store verwenden
        if (to && to.query && (to.query.namedFilter === 'Today:VorOrtTermine' || to.query.namedFilter === 'Today:Wiedervorlagen')) {
            // Hole den Systemfilter direkt aus dem Store
            const filter = businessPartnersStore.namedFilter(to.query.namedFilter);
            // Setze den Filter explizit im Store
            userStore.currentSelection.businessPartnerList.filter = { ...filter };
            // Lade die Daten mit dem Systemfilter
            await businessPartnersStore.getByFilter(filter, [], false, { userApplied: false });
            // Navigiere mit der neuen Filter-Signatur
            const navTarget = {
                name: 'BusinessPartnerList',
                query: {
                    source: 'filter',
                    hideFilter: 'true',
                    filter: filter.signature
                }
            };
            await router.push(navTarget);
            return;
        }
        // Direktes Routing für SalesPitchFeedback und SalesPitchFollowUp
        if (to && to.path && (to.path === '/salesPitchFeedback' || to.path === '/salesPitchFollowUp')) {
            showFullscreenLoading.value = true;
            try {
                await router.push({ path: to.path });
            } finally {
                showFullscreenLoading.value = false;
            }
            return;
        }
    showFullscreenLoading.value = true;
    try {
        // Zuletzt aktualisierten Nutzer-Filter verwenden (per update-Timestamp)
        let lastUserEntry = null;
        let bestUpdate = -1;
        const lists = businessPartnersStore.businessPartnerLists;
        if (Array.isArray(lists) && lists.length > 0) {
            for (const entry of lists) {
                if (entry && entry.isUserFilter === true) {
                    const upd = typeof entry.update === 'number' ? entry.update : 0;
                    if (upd > bestUpdate) { lastUserEntry = entry; bestUpdate = upd; }
                }
            }
        }
        // Kein Nutzer-Filter vorhanden – nichts zu tun
        if (!lastUserEntry) {
            showFullscreenLoading.value = false;
            return;
        }
        // Immer das vollständige Filterobjekt des letzten User-Filters verwenden
        let filterObj = lastUserEntry && lastUserEntry.filter ? { ...lastUserEntry.filter } : null;
        let userApplied = false;
        if (lastUserEntry && lastUserEntry.isUserFilter === true) {
            userApplied = true;
        }
        if (!filterObj || !filterObj.signature) {
            // Fallback: baue initialen Filter mit gültiger signature
            const salesAgentId = userStore.currentSelection.salesAgent?.id || 0;
            const salesTeamId = userStore.currentSelection.salesTeam?.id || 0;
            const campaignId = userStore.currentSelection.campaign?.id || 0;
            const filterSignature = [
                `idBusinessPartnerList:-1`,
                `idBusinessPartnerStatus:null`,
                `idBusinessPartnerPotential:-1`,
                `idBusinessPartnerProduct:[]`,
                `idBusinessPartnerProductSegment:[]`,
                `idBusinessPartnerCityZip:-1`,
                `nameBusinessPartnerSector:`,
                `idBusinessPartnerOrder:-1`,
                `${salesAgentId}:${salesTeamId}:${campaignId}`
            ].join('|');
            filterObj = {
                idBusinessPartnerStatus: null,
                idBusinessPartnerAboStatus: -1,
                idBusinessPartnerList: -1,
                idBusinessPartnerProduct: [],
                idBusinessPartnerProductSegment: [],
                idBusinessPartnerPotential: -1,
                idBusinessPartnerCityZip: -1,
                nameBusinessPartnerSector: '',
                idBusinessPartnerOrder: -1,
                error: false,
                isOpened: false,
                salesValues: { total: null, gs: null, omp: null, oe: null, tb: null },
                businessPartnerSalesPitchStatus: { id: -1, dateStart: null, dateEnd: null },
                invoice: { dateStart: null, dateEnd: null },
                produkteInclude: {},
                produkteExclude: {},
                produktKombiInclude: {},
                produktKombiExclude: {},
                order: 'DESC',
                search: { mode: 'name', string: '', wholeWord: 0 },
                page: 1,
                pages: 1,
                pageSize: 24,
                signature: filterSignature
            };
        } else {
            filterObj = normalizeBusinessPartnerFilter(filterObj);
        }
        // Utility: Normalisiert alle optionalen Felder, Arrays und Objekte für Filter
        function normalizeBusinessPartnerFilter(filter) {
            const f = { ...filter };
            // Standardwerte für optionale Felder
            if (typeof f.idBusinessPartnerAboStatus === 'undefined') f.idBusinessPartnerAboStatus = -1;
            if (typeof f.idBusinessPartnerCityZip === 'undefined') f.idBusinessPartnerCityZip = -1;
            if (typeof f.nameBusinessPartnerSector === 'undefined') f.nameBusinessPartnerSector = '';
            if (typeof f.salesValues === 'undefined') f.salesValues = { total: null, gs: null, omp: null, oe: null, tb: null };
            if (typeof f.businessPartnerSalesPitchStatus === 'undefined') f.businessPartnerSalesPitchStatus = { id: -1, dateStart: null, dateEnd: null };
            if (typeof f.invoice === 'undefined') f.invoice = { dateStart: null, dateEnd: null };
            if (typeof f.produkteInclude === 'undefined') f.produkteInclude = {};
            if (typeof f.produkteExclude === 'undefined') f.produkteExclude = {};
            if (typeof f.produktKombiInclude === 'undefined') f.produktKombiInclude = {};
            if (typeof f.produktKombiExclude === 'undefined') f.produktKombiExclude = {};
            if (typeof f.idBusinessPartnerOrder === 'undefined') f.idBusinessPartnerOrder = -1;
            if (typeof f.order === 'undefined') f.order = 'DESC';
            if (typeof f.search === 'undefined') f.search = { mode: 'name', string: '', wholeWord: 0 };
            // Arrays sortieren
            if (Array.isArray(f.idBusinessPartnerProduct)) {
                f.idBusinessPartnerProduct = f.idBusinessPartnerProduct
                    .map(x => typeof x === 'object' ? x : { id: x, include: false })
                    .sort((a, b) => (a.id > b.id ? 1 : -1));
            }
            if (Array.isArray(f.idBusinessPartnerProductSegment)) {
                f.idBusinessPartnerProductSegment = f.idBusinessPartnerProductSegment
                    .map(x => typeof x === 'object' ? x : { id: x, include: false })
                    .sort((a, b) => (a.id > b.id ? 1 : -1));
            }
            // Objekte nach Key sortieren
            function sortObject(obj) {
                return Object.keys(obj).sort().reduce((acc, key) => { acc[key] = obj[key]; return acc; }, {});
            }
            f.produkteInclude = sortObject(f.produkteInclude);
            f.produkteExclude = sortObject(f.produkteExclude);
            f.produktKombiInclude = sortObject(f.produktKombiInclude);
            f.produktKombiExclude = sortObject(f.produktKombiExclude);
            // searchCriteria normalisieren
            if (Array.isArray(f.searchCriteria)) {
                f.searchCriteria = f.searchCriteria.filter(sc =>
                    typeof sc.value !== 'undefined' && sc.value !== null && sc.value !== ''
                );
            }
            return f;
        }
        // Immer neue Anfrage schicken, damit Backend und Store synchron sind.
        // searchCriteria aus dem gespeicherten Eintrag verwenden (NICHT []) –
        // sonst wird z.B. PLZ/ORT (idBusinessPartnerCityZip) aus searchCriteria gelöscht,
        // weil es im Backend-Payload über view.searchCriteria übermittelt wird.
        const storedSearchCriteria = (lastUserEntry && Array.isArray(lastUserEntry.searchCriteria))
            ? JSON.parse(JSON.stringify(lastUserEntry.searchCriteria))
            : [];
        await businessPartnersStore.getByFilter(filterObj, storedSearchCriteria, false, { userApplied });
        // Setze den Filter im userStore
        if (filterObj) {
            userStore.currentSelection.businessPartnerList.filter = { ...filterObj };
        }
        // Ziel für Navigation bauen
        // isUserFilter: 'true' → routes.js setzt userApplied:true → Store speichert isUserFilter:true
        const navTarget = {
            name: 'BusinessPartnerList',
            query: {
                source: 'filter',
                hideFilter: 'true',
                filter: filterObj.signature || lastUserFilterSignature.value,
                isUserFilter: 'true'
            }
        };
        await router.push(navTarget);
    } finally {
        showFullscreenLoading.value = false;
    }
}

// Hilfsfunktion: Suche nach Filterobjekt anhand der Signatur
function findFilterBySignature(signature) {
    // Suche in gespeicherten Filtern, falls vorhanden
    // Hier kannst du die Logik anpassen, falls du Filter im Store oder localStorage speicherst
    // Beispiel: Suche in userStore.filterHistory oder businessPartnersStore.businessPartnerLists
    if (userStore.filterHistory && Array.isArray(userStore.filterHistory)) {
        return userStore.filterHistory.find(f => f.signature === signature);
    }
    if (businessPartnersStore.businessPartnerLists && Array.isArray(businessPartnersStore.businessPartnerLists)) {
        const found = businessPartnersStore.businessPartnerLists.find(f => f.signature === signature);
        if (found && found.filter) return found.filter;
    }
    // Fallback: kein Filterobjekt gefunden
    return null;
}


// Button ist aktiv, sobald ein echter Nutzer-Filter vorhanden ist (auch mit 0 Treffern)
const isFilterButtonActive = computed(() => {
    return lastUserFilterEntry.value !== null;
});

// Computed für aktuelle Trefferzahl aus Store
                const bpListCount = computed(() => businessPartnersCountStore.businessPartnersListsCountBySignature(bpListCountSignature.value) || 0);
</script>

<template>
    <div class="preview-bp-block potential-bk">
            <preview-header
                title="Potential & BK"
                :hit-count="bpListCount"
                button-label="Zur vollen Liste"
                :disabled="isPotentialBKDisabled || isBpListCountLoading || bpListCount === 0 || userStore.currentSelection.campaign?.isLoading"
                :loading-disabled="userStore.currentSelection.businessPartnerList.isLoading"
                @click="handleWidgetNavigationInitialFilter"
                @keydown.enter="handleWidgetNavigationInitialFilter"
            >
            <template #icon>
                <span class="number-label sm pill-count-spinner">
                    <template v-if="isBpListCountLoading">
                        <InlineSpinner size="sm" label="Wird geladen…" />
                    </template>
                    <template v-else>
                        {{ formatCountWithThousands(bpListCount) }}
                    </template>
                
                </span>
            </template>
        </preview-header>
        <!-- <div v-if="isPotentialBKDisabled" class="potential-bk-disabled-reason">
            <span class="disabled-reason-text">{{ potentialBKDisabledReason }}</span>
        </div> -->
        <div id="potential-bk" :class="['widget-wrapper potential-bk simple-preview', {'exceeded': (exceededToDos && (exceededToDos.feedbacks || exceededToDos.followUps))}]">
            <!-- Action Cards -->
            <div class="container-wrapper action-cards-wrapper">
            <!-- {{ lastUserFilterEntry }} -->
            <!-- {{ lastUserFilterChips }} -->
                <div class="container main-content">
                    <div class="action-cards two-col" role="list">
                        <!-- Neue Card als erstes Element in den Action Cards, jetzt im Standard-Card-Design -->
                        <div class="action-card card-pointer" style="grid-column:span 2;" @click="handleWidgetNavigation()" :aria-disabled="isBpListCountLoading || isPotentialBKDisabled || !isFilterButtonActive" tabindex="0" @keydown.enter="handleWidgetNavigation()"
                            :class="{ 'is-disabled': !isFilterButtonActive }">
                            <div class="ac-top ac-top-toggle">
                                <span class="ac-label">Zur gefilterten Liste</span>
                                <span class="number-label sm" style="margin-left:0.5rem">{{ lastUserFilterEntry ? formatCountWithThousands(lastBusinessPartnerListCount) : '' }}</span>
                                <span class="chip-arrow-toggle pointer" role="button" tabindex="0" :aria-expanded="showChips.toString()" aria-controls="filter-chips-summary"
                                    @click.stop="showChips = !showChips" @keydown.enter.stop.prevent="showChips = !showChips">
                                    <span class="chip-arrow-label-inline">Filter</span>
                                    <svg :style="{ transform: showChips ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path d="M8 10l4 4 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </span>
                            </div>
                            <div v-if="lastUserFilterEntry && lastUserFilterChips.length > 0" class="ac-chips-row" style="margin-bottom:0.2rem;">
                                <div id="filter-chips-summary">
                                    <FilterSummaryChips hideCount :alwaysShowText="showChips" :summaryObj="lastUserFilterChips" />
                                </div>
                            </div>
                            <div class="ac-stats-row">
                                <template v-if="userStore.currentSelection.businessPartnerList.isLoading">
                                    <InlineSpinner size="sm" label="Wird geladen…" />
                                </template>
                                <template v-else-if="lastUserFilterEntry === null">
                                    <div class="ac-empty-msg">Noch keinen Filter verwendet</div>
                                </template>
                                <template v-else>
                                    <div class="ac-count">{{ formatCountWithThousands(lastBusinessPartnerListCount) }}</div>
                                </template>
                            </div>
                        </div>
                        <div
                            v-for="qa in quickActions"
                            :key="qa.key"
                            :class="['action-card', { 'is-disabled': qa.disabled, 'is-negative': qa.negative }]"
                            role="listitem"
                            :aria-busy="qa.loading.toString()"
                            @click.prevent="handleWidgetNavigation(qa.to)"
                            tabindex="0"
                            @keydown.enter="handleWidgetNavigation(qa.to)"
                        >
                            <div class="ac-top">
                                <span class="ac-label">{{ qa.label }}</span>
                            </div>
                            <div class="ac-stats-row">
                                <template v-if="qa.loading">
                                    <InlineSpinner size="sm" label="Wird geladen…" />
                                </template>
                                <template v-else>
                                    <div class="ac-count" :class="{'pulse': qa.negative && qa.count>0}">{{ qa.count }}</div>
                                    <div v-if="qa.max !== null" class="ac-max">/ {{ qa.max }}</div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Warning Banner -->
            <div v-if="exceededToDos && (exceededToDos.feedbacks || exceededToDos.followUps)" class="container">
                <div class="warningText">
                    <div class="warning-banner">
                        <template v-if="!beforeCampaignEnd">
                            <p v-if="exceededToDos.both">Die maximale Anzahl unbearbeiteter Termine von {{ quickActions.find(q => q.key === 'terminFeedback')?.max }} und Wiedervorlagen von {{ quickActions.find(q => q.key === 'followUpTodo')?.max }} wurde erreicht. Bitte bearbeite Feedback und Wiedervorlagen, um fortzufahren.</p>
                            <p v-else-if="exceededToDos.followUps">Die maximale Anzahl unbearbeiteter Wiedervorlagen von {{ quickActions.find(q => q.key === 'followUpTodo')?.max }} wurde erreicht. Bitte bearbeite ältere Wiedervorlagen, um fortzufahren.</p>
                            <p v-else>Die maximale Anzahl unbearbeiteter Termine von {{ quickActions.find(q => q.key === 'terminFeedback')?.max }} wurde erreicht. Bitte bearbeite Feedback, um fortzufahren.</p>
                        </template>
                        <template v-else>
                            <p>Die maximale Anzahl unbearbeiteter Termine von {{ quickActions.find(q => q.key === 'terminFeedback')?.max }} wurde erreicht. Bitte bearbeite Feedback, um fortzufahren.</p>
                        </template>
                    </div>
                </div>
            </div>

            <!-- Leads Dropdown / Loading -->
            <div class="container-wrapper loading-overlay-parent" v-if="!exceededToDos.feedbacks && !exceededToDos.followUps" style="position:relative;">
                <div class="container">
                    <div v-if="isLeadListsLoading" class="empty-leads-info">
                        <span class="spinner-with-text">
                            <InlineSpinner size="sm" label="Sugar Aktionen werden geladen…" />
                            <span class="hint">Sugar Aktionen werden geladen…</span>
                        </span>
                    </div>
                    <div v-else-if="hasLeadLists">
                        <div class="custom-dropdown form-child">
                            <LeadListDropdown
                                :options="leadListOptions || []"
                                :trigger-label="'Unb. Sugar Aktionen'"
                                :placeholder="`(${(Array.isArray(leadListOptions) ? leadListOptions : []).reduce((a,l)=>a+(l.businessPartnerCount||0),0)})`"
                                @select="opt => opt && handleSelectList(`list:${opt.id}`)"
                            />
                        </div>
                    </div>
                    <div v-else class="empty-leads-info">
                        <span class="hint">Keine Sugar Aktionen verfügbar.</span>
                    </div>
                </div>
            </div>
                    
            <!-- Search + Recent -->
            <div class="container-wrapper" v-if="!exceededToDos.feedbacks && !exceededToDos.followUps">
                        <LoadingOverlay :show="isSearching" label="Kunde wird gesucht…" spinner-size="lg" />
                <div class="container singleInput">
                    <div :class="['search-area', {'noResults': hasNoResults}]">
                        <div class="search-panel">
                            <div class="search-line">
                                <select class="search-mode" v-model="businessPartnerSearchMode" @change="handleBusinessPartnerSearch" aria-label="Suchmodus">
                                    <option v-for="m in searchModeOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
                                </select>
                                <button type="button" class="search-flag" :class="{active: wholeWord}" @click="wholeWord = !wholeWord" :aria-pressed="wholeWord.toString()" aria-label="Ganzes Wort suchen">
                                    <img :src="wholeWordIcon" alt="Ganzes Wort" />
                                </button>
                            </div>
                            <div class="search-input-wrap">
                                <input class="search-input" :inputmode="['GPNo','tel'].includes(businessPartnerSearchMode) ? 'numeric' : 'text'" v-model="businessPartnerSearch" :placeholder="searchPlaceholderText" @keyup.enter.prevent="handleBusinessPartnerSearch" autocomplete="off" />
                                <button v-if="businessPartnerSearch" type="button" class="search-clear" @click="clearSearch" aria-label="Eingabe löschen">✕</button>
                                <button
                                  type="button"
                                  class="search-submit"
                                  :disabled="!isValidSearch || isSearching"
                                  :aria-busy="isSearching.toString()"
                                  @click="handleBusinessPartnerSearch"
                                  aria-label="Suche ausführen"
                                >
                                    <span class="icon-wrap" aria-hidden="true">
                                        <svg class="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2"/>
                                            <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                        </svg>
                                    </span>
                                </button>
                            </div>
                            <div class="search-hint" v-if="businessPartnerSearch && !isValidSearch">
                                <span>Mind. {{ businessPartnerSearchMode === 'name' ? '2' : (businessPartnerSearchMode === 'GPNo' ? '3' : '6') }} Zeichen für {{ businessPartnerSearchMode }}</span>
                            </div>
                            <div class="recent-searches-wrapper">
                                <div class="rs-headline-row">
                                    <h6 class="rv-title">LETZTE SUCHBEGRIFFE</h6>
                                    <button type="button" class="rs-clear" @click="clearRecentSearches" title="Alle löschen" aria-label="Alle löschen" v-if="recentSearches.length>1">Alles löschen</button>
                                </div>
                                <ul class="recent-searches" role="list" :aria-disabled="searchLoading.toString()">
                                    <li v-for="q in recentSearches" :key="q" role="listitem" :class="{'is-active': q.toLowerCase()===lastQuery.toLowerCase(), 'is-disabled': searchLoading}">
                                        <button type="button" class="rs-item-btn" @click="handleRecentClick(q)" :disabled="searchLoading" :aria-busy="(searchLoading && q.toLowerCase()===lastQuery.toLowerCase()).toString()">
                                            <span class="lbl ellipsis" :title="q" v-if="!(searchLoading && q.toLowerCase()===lastQuery.toLowerCase())">{{ q }}</span>
                                            <InlineSpinner v-else size="sm" label="Suche läuft…" />
                                        </button>
                                        <button type="button" class="rs-remove" @click="removeRecentSearch(q)" aria-label="Entfernen" title="Entfernen" :disabled="searchLoading">
                                            <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M18.3 5.71L12 12.01l-6.3-6.3-1.42 1.42 6.3 6.29-6.3 6.3 1.42 1.42 6.29-6.3 6.3 6.3 1.42-1.42-6.3-6.3 6.3-6.29z"/></svg>
                                        </button>
                                    </li>
                                    <li v-if="recentSearches.length === 0" class="rv-empty" aria-disabled="true">Noch keine Suchbegriffe.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    <LoadingOverlay :show="showFullscreenLoading" label="Daten werden geladen…" spinner-size="lg" />
    </div>
</template>


<style scoped>
.chip-arrow-toggle { display:inline-flex; align-items:center; gap:0.25rem; font-size:0.55rem; color:inherit; cursor: pointer;}
.chip-arrow-toggle .chip-arrow-label-inline { font-size: .48rem;
  font-weight: 600;
  letter-spacing: .45px;
  text-transform: uppercase;
  opacity: .75; }
.action-card .summary-chips .span { font-size: .42rem; padding: .1rem .35rem; }
.preview-bp-block { display:flex; flex-direction:column; gap:.55rem; }
.action-cards.two-col { display:grid; gap:.6rem; grid-template-columns:repeat(2,1fr); }
.action-card { cursor: pointer; position:relative; background:linear-gradient(145deg,#222a33,#1b2229); border:1px solid #303943; border-radius:14px; padding:.6rem .65rem .65rem; display:flex; flex-direction:column; text-decoration:none; color:#d5dae3; transition:.25s background,.25s transform,.25s border-color,.25s box-shadow; overflow:hidden; min-height: 60px;}
.action-card:before { content:""; position:absolute; inset:0; background:radial-gradient(circle at 110% 8%,rgba(255,255,255,.08),transparent 70%); pointer-events:none; }
.action-card:hover { background:linear-gradient(145deg,#27323c,#1f272e); transform:translateY(-3px); box-shadow:0 10px 28px -16px rgba(0,0,0,.55),0 4px 14px -6px rgba(0,0,0,.45); }
.action-card.is-disabled { opacity:.45; pointer-events:none; cursor: not-allowed; }
.action-card.is-negative { border-color:#b91c1c; }
.ac-top { display:flex; justify-content:space-between; align-items:center; }
.ac-label { font-size:.48rem; font-weight:600; letter-spacing:.45px; text-transform:uppercase; opacity:.75; }
.ac-stats-row { display:flex; align-items:flex-end; margin-top:.25rem; }
.ac-count { font-size:1.3rem; font-weight:600; line-height:1; color:var(--sellwerk-color-secondary,#36b4e8); }
.action-card.is-negative .ac-count { color:#dc2626; }
.ac-max { font-size:.55rem; opacity:.55; margin-left:.35rem; line-height:1.1; }
.loader.tiny { width:12px; height:12px; }
.pulse { animation:pulse 1.8s infinite; }
@keyframes pulse { 0% { text-shadow:0 0 0 rgba(220,38,38,.4); } 50% { text-shadow:0 0 14px rgba(220,38,38,.55); } 100% { text-shadow:0 0 0 rgba(220,38,38,.4); } }
/* Unify warning-banner to look like .form-errors */
.warning-banner p {
    padding: .5rem .6rem;
    border-radius: 10px;
    border: 1px solid #8b1f1f;
    background: #2a1717;
    color: #fecaca;
    font-size: .7rem;
    list-style: disc;
    max-width: 100% !important;
}
.theme--light .warning-banner p {
    background: #fff1f2;
    border-color: #fda4af;
    color: #7f1d1d;
}
.theme--light .action-card { background:linear-gradient(135deg,#ffffff,#f4f7fa); border-color:#d5dde5; color:#1e293b; }
.theme--light .action-card:hover { background:linear-gradient(135deg,#f5f9fc,#eaf1f6); }
.theme--light .action-card.is-negative { border-color:#dc2626; }
.number-label { display:inline-flex; align-items:center; justify-content:center; font-weight:600; line-height:1; background:#31404b; color:#d5dae3; padding:.18rem .55rem .22rem; font-size:.52rem; border-radius:999px; letter-spacing:.3px; white-space:nowrap; }
.number-label.xs { font-size:.48rem; padding:.14rem .42rem .16rem; }
.number-label.contrast { background:#1790da; color:#fff; }
.number-label.new { background:#b91c1c; color:#fff; }
.number-label.mid { background:#0d4d72; color:#e2f4ff; }
.number-label.high { background:linear-gradient(135deg,#0369a1,#0ea5e9); color:#fff; }
.theme--light .number-label { background:#e5edf3; color:#1e293b; }
.theme--light .number-label.contrast { background:var(--sellwerk-color-secondary,#1790da); color:#fff; }
.theme--light .number-label.new { background:#dc2626; color:#fff; }
.theme--light .number-label.mid { background:#1677c5; color:#fff; }
.theme--light .number-label.high { background:linear-gradient(135deg,#1677c5,#36b4e8); color:#fff; }
.custom-dropdown { position:relative; }
.dropdown-toggle { all:unset; cursor:pointer; font-size:.6rem; font-weight:600; letter-spacing:.45px; display:block; width:100%; background:#242c34; border:1px solid #313b46; padding:.55rem .75rem; border-radius:12px; }
.theme--light .dropdown-toggle { background:#ffffff; border-color:#ccd5dd; color:#1e293b; }
.dropdown-menu { position:absolute; top:100%; left:0; right:0; z-index:40; margin:.35rem 0 0; padding:.4rem; background:#1d232a; border:1px solid #313b46; border-radius:12px; display:flex; flex-direction:column; gap:.25rem; }
.theme--light .dropdown-menu { background:#ffffff; border-color:#d5dde5; }
.dropdown-item { list-style:none; font-size:.55rem; padding:.42rem .55rem; border-radius:8px; cursor:pointer; display:flex; justify-content:space-between; }
.dropdown-item.empty { opacity:.55; cursor:default; }
.dropdown-item:hover { background:#28313a; }
.theme--light .dropdown-item:hover { background:#f2f6fa; }
.empty-leads-info .hint { font-size:.95rem; }
.spinner-with-text { display:inline-flex; align-items:center; gap:.4rem; }
.search-wrapper { display:flex; flex-direction:column; gap:.35rem; width:100%; }
.search-wrapper.noResults input { border-color:#b91c1c; }
.search-wrapper .warning-txt { font-size:.55rem; color:#dc2626; }
.search input, .search-input { padding:.45rem .75rem; border:1px solid #313844; background:#161b21; color:#e2e8f0; border-radius:16px; font-size:.65rem; min-width:220px; }
.theme--light .search input, .theme--light .search-input { background:#fff; border-color:#ccd5dd; color:#1e293b; }
.recent-searches-wrapper { margin-top:.65rem; display:flex; flex-direction:column; gap:.4rem; width:max-content; max-width:100%; }
.rs-headline-row { display:flex; align-items:center; gap:.75rem; }
.rs-clear { all:unset; cursor:pointer; font-size:.48rem; letter-spacing:.4px; font-weight:500; color:#7c8a96; padding:.25rem .55rem; background:#202830; border:1px solid #2d3842; border-radius:999px; line-height:1; transition:.2s background,.2s color,.2s border-color; }
.rs-clear:hover { background:#27323c; color:#d5dae3; }
.rs-clear:focus-visible { outline:2px solid var(--sellwerk-color-secondary,#1790da); outline-offset:2px; }
.theme--light .rs-clear { background:#f1f5f9; border-color:#d3dbe3; color:#475569; }
.theme--light .rs-clear:hover { background:#e6eef3; color:#1e293b; }
.recent-searches { list-style:none; margin:0; padding:0; display:flex; flex-wrap:wrap; gap:.35rem; }
.recent-searches li { position:relative; display:flex; align-items:center; background:#1d232a; border-radius:999px; padding: 0 .55rem; gap:.45rem; min-height:26px; max-width:100%; border: 1px solid #fff; }
.recent-searches li.is-active { background:linear-gradient(135deg,#1f2a33,#233542); }
.theme--light .recent-searches li { background:#ffffff; border-color:#d5dde5; }
.theme--light .recent-searches li.is-active { background:linear-gradient(135deg,#eef5fa,#e2edf5); }
.rs-item-btn { all:unset; cursor:pointer; display:flex; align-items:center; min-width:0; width:max-content; }
.lbl { font-size:.55rem; font-weight:500; letter-spacing:.3px; line-height:1.2; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.rs-remove { all:unset; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; width:20px; height:20px; border-radius:6px; color:#8896a3; transition:.2s background,.2s color; }
.rs-remove:focus-visible { outline:2px solid var(--sellwerk-color-secondary,#1790da); outline-offset:2px; }
.theme--light .rs-remove { color:#64748b; }
.spinner { width:16px; height:16px; border:2px solid rgba(255,255,255,0.35); border-top-color:var(--sellwerk-color-secondary,#1790da); border-radius:50%; animation:spin .55s linear infinite; }
.spinner.small { width:14px; height:14px; border-width:2px; }
.theme--light .spinner { border-color:rgba(0,0,0,0.2); border-top-color:var(--sellwerk-color-main,#1677c5); }
@keyframes spin { to { transform:rotate(360deg); } }
.pulse { animation:pulse 1.8s infinite; }
.sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0 0 0 0); border:0; }
/* .pill-count-spinner {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.6em;
    height: 1.4em;
    font-size: 0.85em;
    padding: 0 0.25em;
    margin-left: 0.4em;
    background: var(--pill-bg, #f3f3f3);
    border-radius: 1em;
    box-sizing: border-box;
}
.pill-count-spinner .inline-spinner {
    margin: 0;
    height: 1em;
    width: 1em;
} */

/* Overlay parent should fill available space and allow overlay to center */
.loading-overlay-parent {
    position: relative;
    /* min-height: 120px; */
    display: flex;
    flex-direction: row;
    justify-content: center;
}
.loading-leads-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    /* min-height: 120px; */
    background: rgba(255,255,255,0.7);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: all;
}
.spinner-center {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}
.spinner-center .loadingDescription {
    font-weight: 600;
   
}
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s;
}
.fade-enter-from, .fade-leave-to {
    opacity: 0;
}
</style>
